import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import type { LocaleKey } from "../utils/svgPlaceholders";

interface LanguageSwitcherProps {
  locale: LocaleKey;
}

const computeSwitchPath = (pathname: string, locale: LocaleKey): string => {
  if (locale === "en") {
    const next = pathname.replace(/^\/en/, "") || "/";
    return next.endsWith("/") ? next : `${next}/`;
  }

  if (pathname === "/") {
    return "/en/";
  }

  return pathname.startsWith("/en") ? pathname : `/en${pathname}`;
};

export const LanguageSwitcher = ({ locale }: LanguageSwitcherProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const targetLocale: LocaleKey = locale === "en" ? "zh-Hant" : "en";

  const targetPath = computeSwitchPath(location.pathname, locale);

  const graphicSrc =
    locale === "en"
      ? "/basiclaw2025-26/assets/menu/language-btn.png"
      : "/basiclaw2025-26/assets/menu/language-btn-en.png";

  const MotionLink = motion(Link);

  return (
    <MotionLink
      to={targetPath}
      className="language-switcher"
      aria-label={
        targetLocale === "en"
          ? t("layout.languageSwitch.switchToEnglish")
          : t("layout.languageSwitch.switchToTraditionalChinese")
      }
      whileHover={{ scale: [null, 1.2, 1.1] }}
      whileTap={{ scale: 1.05 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <img
        src={graphicSrc}
        alt={
          targetLocale === "en"
            ? t("layout.languageSwitch.english")
            : t("layout.languageSwitch.traditionalChinese")
        }
        className="language-switcher__image"
      />
    </MotionLink>
  );
};
